import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import axios from "axios";
import { 
  Search, 
  FileText, 
  Loader2,
  Calendar,
  Building,
  CheckCircle
} from "lucide-react";

export default function FormLibrary() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [insurerFilter, setInsurerFilter] = useState("all");
  const [insurers, setInsurers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [formsRes, insurersRes] = await Promise.all([
          axios.get(`${API}/forms`),
          axios.get(`${API}/insurers`)
        ]);
        setForms(formsRes.data);
        setInsurers(insurersRes.data);
      } catch (error) {
        console.error("Error fetching forms:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredForms = forms.filter(form => {
    const matchesSearch = form.form_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         form.insurer?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesInsurer = insurerFilter === "all" || form.insurer === insurerFilter;
    return matchesSearch && matchesInsurer;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="form-library-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Form Library</h1>
          <p className="text-slate-400">Browse and view insurance PA forms</p>
        </div>

        {/* Filters */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search forms..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="form-search"
                />
              </div>
              <Select value={insurerFilter} onValueChange={setInsurerFilter}>
                <SelectTrigger className="w-full sm:w-48 bg-slate-800 border-slate-700 text-white" data-testid="insurer-filter">
                  <Building className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by insurer" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="all">All Insurers</SelectItem>
                  {insurers.map(insurer => (
                    <SelectItem key={insurer} value={insurer}>{insurer}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Forms Grid */}
        {filteredForms.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredForms.map((form) => (
              <Card key={form.form_id} className="bg-slate-900/50 border-slate-800 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/10">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-emerald-400" />
                    </div>
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                      v{form.version}
                    </Badge>
                  </div>
                  <CardTitle className="text-base mt-3 text-white">{form.form_name}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Building className="w-4 h-4" />
                      <span>{form.insurer}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                      <Calendar className="w-4 h-4" />
                      <span>Updated: {new Date(form.updated_at).toLocaleDateString()}</span>
                    </div>
                    {form.specialty && (
                      <Badge className="mt-2 bg-slate-700 text-slate-300 border-0">{form.specialty}</Badge>
                    )}
                  </div>

                  <Accordion type="single" collapsible className="mt-4">
                    <AccordionItem value="fields" className="border-slate-700">
                      <AccordionTrigger className="text-sm py-2 hover:no-underline text-slate-300">
                        View {form.fields?.length || 0} required fields
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2">
                          {form.fields?.map((field, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                              <CheckCircle className="w-3 h-3 text-emerald-400" />
                              <span>{field.label}</span>
                              {field.required && (
                                <span className="text-red-400 text-xs">*</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-400">
                {searchQuery || insurerFilter !== "all" 
                  ? "No forms found matching your filters" 
                  : "No forms available"}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
